import { PkButton } from '@uefadigital/panenka-react';
import { useState, useEffect, useMemo, memo } from 'react';

import { useCalendarServiceOptions, useCalendarServiceStore } from '../../store';
import { trackView } from '../../utils';

import { useCountry } from '@hooks/useCountry';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { language, IS_MOBILE } from '@utilities/browser';
import { formatUrl } from '@utilities/fetch';

const isIOs = /iPhone|iPad|iPod/i.test(navigator.userAgent);

const howToConfig = [
  { tag: 'outlook', length: 4 },
  { tag: 'google_calendar', length: 3 },
] as const;

const DownloadContent = () => {
  const {
    options: { competitionId, calendarServiceApiUrl },
  } = useCalendarServiceOptions();
  const selectedTeams = useCalendarServiceStore((state) => state.selectedTeams);
  const country = useCountry();
  const t = useTranslateFn();
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  useEffect(() => {
    trackView('downloadCalendar', 'confirmation');
  }, []);

  const calendarServiceUrl = useMemo(() => {
    if (!country) return;

    return formatUrl(calendarServiceApiUrl, 'v1/calendar.ics', {
      teamId: selectedTeams,
      competitionId,
      language: language?.toUpperCase(),
      reminder: 60,
      countryCode: country?.countryCodeShort?.toUpperCase(),
    }).replace('http://', 'https://');
  }, [calendarServiceApiUrl, competitionId, country, selectedTeams]);

  useEffect(() => {
    if (!calendarServiceUrl || !IS_MOBILE) return;

    const link = document.createElement('a');
    link.href = isIOs ? calendarServiceUrl.replace('https://', 'webcal://') : calendarServiceUrl;
    link.download = 'calendar.ics';
    link.target = '_blank';
    link.click();
  }, [calendarServiceUrl]);

  const handleCopyClick = () => {
    GoogleTagManagerHelper.ga4Event('calendar', {
      section_reference: 'addtocalendar',
      interaction: 'Download calendar',
    });
    navigator.clipboard.writeText(calendarServiceUrl);
    setShowCopyMessage(true);
  };

  const handleShowHowTo = () => {
    setShowHowTo((val) => !val);
  };

  return (
    <div className='pk-d--flex pk-flex--column pk-align-items--center pk-gap--s pk-pb--m pk-text--text-01'>
      {showCopyMessage ? (
        <div className='pk-d--flex pk-flex--column pk-align-items--center pk-w--100 pk-gap--xs'>
          <div className='pk-font-size--s pk-font--base-bold'>{t('copied_in_clipboard')}</div>
          <input className='pk-w--50 pk-p--s' type='text' readOnly disabled value={calendarServiceUrl} />
        </div>
      ) : (
        <PkButton
          onPkClick={handleCopyClick}
          disabled={!calendarServiceUrl ? true : undefined}
          appearance='outlined'
          width='auto'
        >
          {t('copy_url')}
        </PkButton>
      )}
      <div>
        {t('copy_url_disclaimer')}
        <PkButton onPkClick={handleShowHowTo} className='pk-ml--xs' appearance='text'>
          {t('copy_url_how_to')}
        </PkButton>
      </div>
      {showHowTo && (
        <div className='pk-d--flex pk-flex--wrap pk-gap--m'>
          {howToConfig.map(({ tag, length }) => (
            <div key={tag} className='pk-d--flex pk-flex--column'>
              <span className='pk-font-size--s pk-font--base-bold pk-mb--xs2'>{t(tag)}</span>
              <ul>
                {Array.from({ length }, (_, i) => i).map((i) => (
                  <li className='pk-mt--xs2' key={`${tag}-${i}`}>
                    <span className='pk-mr--xs2 pk-font--base-semibold'>{`${i + 1}.`}</span>
                    <span>{t(`${tag}${i + 1}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(DownloadContent);
